// Stable, explicit model lists for device categories.
// These are the source of truth for what appears in the model picker —
// independent of what services exist in the Google Sheet.
//
// Adding a new model here makes it appear immediately in the picker even
// before Sheet pricing rows exist. Removing a Sheet row never hides a model.
//
// Categories NOT listed here (iphone, ipad, samsung) still derive their
// model lists from Sheet services. Controller categories use a separate
// type picker and are not model-picker-based.

import type { DeviceCategory } from '../types/services';

export interface DeviceModel {
  id: string;    // lowercase slug — must match compatible_models in Sheet (case-insensitive)
  label: string; // display name shown in picker
}

export const STABLE_DEVICE_MODELS: Partial<Record<DeviceCategory, DeviceModel[]>> = {
  ps5: [
    { id: 'ps5',      label: 'PS5' },
    { id: 'ps5 slim', label: 'PS5 Slim' },
    { id: 'ps5 pro',  label: 'PS5 Pro' },
  ],
  xbox: [
    { id: 'series x', label: 'Xbox Series X' },
    { id: 'series s', label: 'Xbox Series S' },
  ],
  switch: [
    { id: 'switch',      label: 'Switch (LCD)' },
    { id: 'switch oled', label: 'Switch OLED' },
    { id: 'switch 2',    label: 'Switch 2' },
  ],
  // handheld-pc intentionally omitted — families and models are derived dynamically
  // from the Google Sheet subcategory/compatible_models columns via handheldFamilyMap.
};

/** Returns the stable model IDs for a category, or null if not a stable-config category. */
export function getStableModels(cat: DeviceCategory): DeviceModel[] | null {
  return STABLE_DEVICE_MODELS[cat] ?? null;
}

/** True if this category uses the stable model picker (not sheet-derived). */
export function hasStableModels(cat: DeviceCategory): boolean {
  return cat in STABLE_DEVICE_MODELS;
}
